import { useBunja } from 'bunja/react';
import cx from 'classnames';
import { atom, useAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useMount } from 'react-use';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { Button } from '@flipster/react/design-system/button';
import DocumentSearchIcon from '@flipster/ui/assets/svgs/document-search.svg';
import { getFlipsterConfigsV1Key, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import segment from '@flipster/utils/segment';
import { PositionEvent } from '@flipster/utils/segment/events/position';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import PositionSectionHeader from './PositionSectionHeader';
import { PositionTable } from './PositionTable';
import styles from './positionSection.module.scss';

export const positionTabTypeAtom = atom<'purchased' | 'pending'>('purchased');

const loggingMap = {
  purchased: 'Purchased' as const,
  pending: 'Pending' as const,
};

const SpotPositionSection = () => {
  const transTrade = useTranslations('Trade.r250401');
  const transCommon = useTranslations('Common.r250401');
  const { symbol } = useBunja(tradeSymbolBunja);

  const [tab, setTab] = useAtom(positionTabTypeAtom);
  const [showAllSymbol, setShowAllSymbol] = useState(true);

  const { isLoggedIn, user } = useAuth();

  const onChangeTab = (selectedTab: 'purchased' | 'pending') => {
    setTab(selectedTab);
    segment.track(PositionEvent.PositionOrderStatusTabClick, {
      tab_type: 'Your holdings',
      execution_stage: loggingMap[selectedTab],
    });
  };

  const onClickSignIn = () => {
    segment.track(PositionEvent.PositionTradeSignInClick, { symbol });
  };

  const toggleShowAllSymbol = () => {
    setShowAllSymbol((prev) => {
      if (user) setLocalStorage(getFlipsterConfigsV1Key(user.userId, 'showAllSpot'), !prev);
      return !prev;
    });
  };

  useEffect(() => {
    if (!user) return;
    const showAllPerp = (getLocalStorage(getFlipsterConfigsV1Key(user.userId, 'showAllSpot')) as boolean) ?? true;
    setShowAllSymbol(showAllPerp);
  }, [user]);

  useMount(() => {
    return () => setTab('purchased');
  });

  return (
    <>
      <PositionSectionHeader
        tab={tab}
        onChangeTab={onChangeTab}
        showAllSymbol={showAllSymbol}
        toggleShowAllSymbol={toggleShowAllSymbol}
      />
      {isLoggedIn ? (
        <div className={styles.wrapper}>
          <PositionTable type={tab} showAllSymbol={showAllSymbol} className={styles.positions} />
        </div>
      ) : (
        <div className={cx(styles.wrapper, styles.signIn)}>
          <DocumentSearchIcon />
          <p>{transTrade('Login_required_to_view_holdings')}</p>
          <Button
            as='a'
            href='/login'
            onClick={onClickSignIn}
            size='small'
            hierarchy='secondary'
            color='neutral'
            label={transCommon('Log_in')}
          />
        </div>
      )}
    </>
  );
};

export default SpotPositionSection;
